import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { get, post, tokens } from "./api";
import type { Role, Usuario } from "./tipos";

interface Contexto {
  usuario: Usuario | null;
  carregando: boolean;
  entrar: (email: string, senha: string) => Promise<void>;
  sair: () => Promise<void>;
  podeAoMenos: (papel: Role) => boolean;
}

const HIERARQUIA: Record<Role, number> = { LEITOR: 1, CONSULTOR: 2, GESTOR: 3, ADMIN: 4 };

const Ctx = createContext<Contexto | null>(null);

export function ProvedorAuth({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    if (!tokens.refresh) {
      setCarregando(false);
      return;
    }
    get<Usuario>("/api/auth/me")
      .then(setUsuario)
      .catch(() => tokens.limpar())
      .finally(() => setCarregando(false));
  }, []);

  const entrar = useCallback(async (email: string, senha: string) => {
    const d = await post<{ accessToken: string; refreshToken: string; usuario: Usuario }>(
      "/api/auth/login",
      { email, senha },
    );
    tokens.salvar(d.accessToken, d.refreshToken);
    setUsuario(d.usuario);
  }, []);

  const sair = useCallback(async () => {
    await post("/api/auth/logout").catch(() => {});
    tokens.limpar();
    setUsuario(null);
  }, []);

  const podeAoMenos = useCallback(
    (papel: Role) => !!usuario && HIERARQUIA[usuario.role] >= HIERARQUIA[papel],
    [usuario],
  );

  const valor = useMemo(
    () => ({ usuario, carregando, entrar, sair, podeAoMenos }),
    [usuario, carregando, entrar, sair, podeAoMenos],
  );

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth precisa estar dentro de <ProvedorAuth>");
  return ctx;
}
